<template>
  <div class="input-document-wrapper" :class="{
    'has-warning': isRequired && !hasValidation && hasValue,
    'has-success': isRequired && hasValidation,
    'is-disabled': isDisabled,
    'has-error': validationError
  }">
    <!-- Label -->
    <label v-if="label" :for="elemId" class="input-label">
      <i v-if="isRequired && !hasValidation && hasValue" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <!-- Document Input with Mask -->
    <div class="document-input-container">
      <!-- Country Flag Indicator -->
      <div v-if="showCountryFlag" class="country-flag-indicator">
        <span class="country-flag">{{ currentCountryFlag }}</span>
        <span class="document-type-badge">{{ documentTypeLabel }}</span>
      </div>

      <!-- Input Mask (only when mask is defined) -->
      <InputMask
        v-if="currentMask"
        v-model="internalValue"
        :id="elemId"
        :mask="currentMask"
        :placeholder="currentPlaceholder"
        :disabled="isDisabled"
        :invalid="(isRequired && !hasValidation && hasValue) || !!validationError"
        fluid
        class="document-input"
        @update:modelValue="handleInput"
        @blur="handleBlur"
      />
      <!-- Plain text input when no mask (e.g. passport) -->
      <InputText
        v-else
        :id="elemId"
        :modelValue="internalValue"
        :placeholder="currentPlaceholder"
        :disabled="isDisabled"
        :invalid="(isRequired && !hasValidation && hasValue) || !!validationError"
        fluid
        class="document-input"
        @update:modelValue="handleInput"
        @blur="handleBlur"
      />
    </div>

    <!-- Validation Error Message -->
    <small v-if="validationError && showValidationError" class="validation-error">
      <i class="pi pi-times-circle"></i>
      {{ validationError }}
    </small>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import InputMask from 'primevue/inputmask';
import InputText from 'primevue/inputtext';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';
import {
  validateCPF, validateCNPJ, validateRUT, validateDNI, validateCI,
  validateSSN, validateNIF, validateNifEs, validateCURP, validateDPI, validateCedula,
  validateCedulaEC, validateRUC
} from '../utils/documentValidation';
import { DOCUMENT_PRESENTATION } from '../composables/useCustomerInternationalConfig';

interface ValidationResult {
  valid: boolean;
  error: string | null;
}

interface DocumentConfig {
  type: string;
  label: string;
  mask: string | null;
  placeholder: string;
  flag: string;
  validate: (value: string) => ValidationResult;
}

interface DocumentTypeChangePayload {
  type: string;
  label: string;
  autoDetected?: boolean;
  countryCode?: string | null;
}

const COMPONENT_NAME = 'InputDocument';

const { t } = useI18n();

const props = defineProps({
  elemId: String,
  placeholder: String,
  label: String,

  // Document type for mask, validation, flag (e.g., 'cpf', 'rut', 'dni')
  // This replaces the old countryCode prop — the parent now resolves the type from the API
  documentType: {
    type: String,
    default: 'cpf'
  },

  // DEPRECATED: kept for backwards compatibility, ignored if documentType is set
  countryCode: {
    type: String,
    default: null
  },

  // Custom mask (overrides automatic mask)
  mask: String,

  // Custom placeholder (overrides automatic placeholder)
  customPlaceholder: String,

  // Validation
  validation: {
    type: Function as any,
    default: () => true
  },
  validateOnBlur: {
    type: Boolean,
    default: true
  },
  showValidationError: {
    type: Boolean,
    default: true
  },

  // Required/Disabled
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },

  // v-model
  modelValue: {
    type: String as any,
    default: ''
  },

  // Show country flag indicator
  showCountryFlag: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'validation': [result: ValidationResult];
  'documentTypeChange': [payload: DocumentTypeChangePayload];
}>();

// Validation functions by document type
const validators: Record<string, (v: string, t: any) => ValidationResult> = {
  cpf: validateCPF,
  cnpj: validateCNPJ,
  rut: validateRUT,
  dni: validateDNI,
  ci: validateCI,
  ssn: validateSSN,
  nif: validateNIF,
  nif_es: validateNifEs,
  curp: validateCURP,
  dpi: validateDPI,
  cedula: validateCedula,
  cedula_ec: validateCedulaEC,
  ruc: validateRUC
};

// Build config entries from DOCUMENT_PRESENTATION with validators attached
const documentConfigs: Record<string, DocumentConfig> = Object.fromEntries(
  Object.entries(DOCUMENT_PRESENTATION).map(([type, pres]) => [
    type,
    {
      type,
      label: (pres as any).label,
      mask: (pres as any).mask,
      placeholder: (pres as any).placeholder,
      flag: (pres as any).flag,
      validate: validators[type] ? (v: string) => validators[type](v, t) : (_v: string) => ({ valid: true, error: null })
    }
  ])
);

// State
const internalValue = ref<string>('');
const isInitializing = ref<boolean>(true);
const validationError = ref<string>('');
const hasBeenBlurred = ref<boolean>(false);
const autoDetectedType = ref<string | null>(null);

// Auto-detect document type from value digits (CPF=11, CNPJ=14)
function detectDocumentTypeFromValue(value: string | undefined | null): string | null {
  if (!value) return null;

  const digits = value.replace(/\D/g, '');

  // CPF: 11 digits
  if (digits.length === 11) {
    return 'cpf';
  }
  // CNPJ: 14 digits
  if (digits.length === 14) {
    return 'cnpj';
  }

  return null;
}

// Computed
const isRequired = computed((): boolean => props.ngRequired === true || props.ngRequired === 'true');
const isDisabled = computed((): boolean => props.disabled === true || props.disabled === 'true');
const hasValue = computed((): boolean => {
  const digits = (internalValue.value || '').replace(/[^0-9Kk]/g, '');
  return digits.length > 0;
});

const currentConfig = computed((): DocumentConfig => documentConfigs[props.documentType] || documentConfigs.cpf);

const currentMask = computed((): string | undefined => {
  const mask = props.mask || currentConfig.value.mask;
  return mask || undefined;
})

const currentPlaceholder = computed((): string => props.customPlaceholder || props.placeholder || currentConfig.value.placeholder);

const currentCountryFlag = computed((): string => currentConfig.value.flag);

const documentTypeLabel = computed((): string => currentConfig.value.label);

const hasValidation = computed((): boolean => {
  if (!hasValue.value) return false;
  return !validationError.value;
});

/**
 * Perform validation based on current country
 */
function performValidation(value: string): ValidationResult {
  if (!value || !hasValue.value) {
    validationError.value = '';
    return { valid: true, error: null };
  }

  const result = currentConfig.value.validate(value);
  validationError.value = result.valid ? '' : (result.error ?? '');

  return result;
}

// Event handlers
function handleInput(newValue: string | null | undefined): void {
  if (isInitializing.value) return;

  log(COMPONENT_NAME, 'handleInput', 'Value:', newValue);

  // Clear validation error while typing (will revalidate on blur)
  if (hasBeenBlurred.value) {
    validationError.value = '';
  }

  emit('update:modelValue', newValue || '');
}

function handleBlur(): void {
  hasBeenBlurred.value = true;

  if (props.validateOnBlur && hasValue.value) {
    const result = performValidation(internalValue.value);
    log(COMPONENT_NAME, 'handleBlur', 'Validation result:', result);
    emit('validation', result);
  }
}

// Watchers
watch(() => props.modelValue, (newValue: string | undefined) => {
  if (isInitializing.value) return;

  log(COMPONENT_NAME, 'watch:modelValue', 'Received:', newValue);
  internalValue.value = newValue || '';

  // Auto-detect document type from value (for CPF/CNPJ distinction)
  const detectedType = detectDocumentTypeFromValue(newValue);
  if (detectedType && detectedType !== autoDetectedType.value && detectedType !== props.documentType) {
    log(COMPONENT_NAME, 'watch:modelValue', 'Auto-detected document type:', detectedType);
    autoDetectedType.value = detectedType;

    // Emit document type change to update parent
    const newConfig = documentConfigs[detectedType];
    if (newConfig) {
      emit('documentTypeChange', {
        type: newConfig.type,
        label: newConfig.label,
        autoDetected: true
      });
    }
  }

  // Revalidate if already blurred
  if (hasBeenBlurred.value && hasValue.value) {
    performValidation(internalValue.value);
  }
});

watch(() => props.documentType, (newType: string, oldType: string) => {
  log(COMPONENT_NAME, 'watch:documentType', 'Changed from', oldType, 'to', newType);

  // Emit document type change
  const newConfig = documentConfigs[newType];
  if (newConfig) {
    emit('documentTypeChange', {
      type: newConfig.type,
      label: newConfig.label
    });
  }

  // Clear value and revalidate if type changed
  if (hasValue.value && hasBeenBlurred.value) {
    internalValue.value = '';
    validationError.value = '';
    emit('update:modelValue', '');
  }
});

// Lifecycle
onMounted(async (): Promise<void> => {
  log(COMPONENT_NAME, 'mounted', 'Initial value:', props.modelValue);

  internalValue.value = (props.modelValue as any) || '';

  // Auto-detect document type from initial value (for CPF/CNPJ distinction)
  const detectedType = detectDocumentTypeFromValue(props.modelValue);
  if (detectedType) {
    log(COMPONENT_NAME, 'mounted', 'Auto-detected document type:', detectedType);
    autoDetectedType.value = detectedType;

    // Use detected type as current config
    const detectedConfig = documentConfigs[detectedType];
    if (detectedConfig) {
      await nextTick();
      isInitializing.value = false;

      // Emit detected document type
      emit('documentTypeChange', {
        type: detectedConfig.type,
        label: detectedConfig.label,
        autoDetected: true,
        countryCode: props.countryCode
      });
      return;
    }
  }

  await nextTick();
  isInitializing.value = false;

  // Emit initial document type
  emit('documentTypeChange', {
    type: currentConfig.value.type,
    label: currentConfig.value.label,
    countryCode: props.countryCode
  });
});
</script>

<style scoped>
.input-document-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.document-input-container {
  display: flex;
  gap: 0.5rem;
  width: 100%;
  align-items: center;
}

.country-flag-indicator {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  background: var(--p-content-hover-bg);
  border: 1px solid var(--p-inputtext-border-color);
  border-radius: var(--p-inputtext-border-radius);
}

.country-flag {
  font-size: 1.2rem;
  line-height: 1;
}

.document-type-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
}

.document-input {
  flex: 1;
  min-width: 0;
}

.validation-error {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--p-red-500);
  font-size: 0.875rem;
}

.validation-error i {
  font-size: 0.875rem;
}

.has-error :deep(.p-inputmask) {
  border-color: var(--p-red-500);
}

.has-warning :deep(.p-inputmask) {
  border-color: var(--p-yellow-500);
}

.has-success :deep(.p-inputmask) {
  border-color: var(--p-green-500);
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}

@media (max-width: 768px) {
  .document-input-container {
    flex-direction: column;
  }

  .country-flag-indicator {
    width: 100%;
    justify-content: center;
  }
}
</style>
